import { Box } from 'src/design/atoms/Box';
import { Loader } from 'src/design/atoms/Loader';
import { Paper } from 'src/design/atoms/Paper';
import { Text } from 'src/design/atoms/Text';
import { UnstyledButton } from 'src/design/atoms/UnstyledButton';
import { TextInput } from 'src/design/atoms/TextInput';
import { IconMapPin } from '@tabler/icons-react';
import React from 'react';
import PlacesAutocomplete, {
  geocodeByAddress,
  getLatLng,
} from 'react-places-autocomplete';
import { logger } from 'src/utils';
import classes from './PlaceSelector.module.css';

export interface ILocation {
  address: string;
  geo: { lat: number; lng: number } | null;
}

// Flexible input type that accepts both ILocation and LocationDto from API
export interface ILocationInput {
  address?: string | null;
  geo?: { lat?: number | null; lng?: number | null } | null;
}

export interface IPlaceSelectorProps {
  initialValue: ILocationInput | string | null | undefined;
  onChange?: (location: ILocation) => void;
  label?: string;
  disabled?: boolean;
}

// Convert initialValue to ILocation format
const toLocation = (
  value: ILocationInput | string | null | undefined,
): ILocation => {
  if (!value) return { address: '', geo: null };
  if (typeof value === 'string') return { address: value, geo: null };
  return {
    address: value.address || '',
    geo:
      value.geo?.lat != null && value.geo?.lng != null
        ? { lat: value.geo.lat, lng: value.geo.lng }
        : null,
  };
};

/** @registry Address/place autocomplete selector. */
export const PlaceSelector: React.FC<IPlaceSelectorProps> = ({
  initialValue,
  onChange,
  label,
  disabled = false,
}) => {
  const [location, setLocation] = React.useState<ILocation>(
    toLocation(initialValue),
  );

  React.useEffect(() => {
    if (location.geo !== null && onChange) {
      onChange(location);
    }
  }, [location]);

  const onSelect = async (address: string) => {
    if (disabled) return;
    try {
      const results = await geocodeByAddress(address);
      const latlong = await getLatLng(results[0]);
      setLocation({ address, geo: latlong });
    } catch (error) {
      logger.error('Error selecting place', { err: error });
    }
  };

  if (disabled) {
    return <TextInput label={label} value={location.address} disabled />;
  }

  return (
    <PlacesAutocomplete
      value={location.address}
      onChange={(value) => setLocation((prev) => ({ ...prev, address: value }))}
      onSelect={onSelect}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <Box className={classes.container}>
          <TextInput
            label={label}
            leftSection={<IconMapPin size={16} />}
            {...getInputProps({
              placeholder: 'Search location...',
            })}
          />
          {(loading || suggestions.length > 0) && (
            <Paper className={classes.dropdown} shadow="md" withBorder>
              {loading && (
                <Box className={classes.loadingItem}>
                  <Loader size="xs" mr="sm" />
                  <Text size="sm" c="dimmed">
                    Loading...
                  </Text>
                </Box>
              )}
              {suggestions.map((suggestion) => (
                <UnstyledButton
                  key={suggestion.placeId}
                  className={classes.suggestionItem}
                  data-active={suggestion.active || undefined}
                  {...getSuggestionItemProps(suggestion)}
                >
                  <IconMapPin size={14} className={classes.suggestionIcon} />
                  <Text size="sm" lineClamp={1}>
                    {suggestion.description}
                  </Text>
                </UnstyledButton>
              ))}
            </Paper>
          )}
        </Box>
      )}
    </PlacesAutocomplete>
  );
};
